import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../query-client';
import { fetchPage } from '../use-notifications-feed';
import { useMorningBrief } from '../brief';
import { useTasks } from '../tasks';
import { useWorkspaceGoals } from '../goals';
import { useAgentWorkspaceFiles } from '../agent-workspace-files';
import { useAllWorkspaceApps } from '../app-shares';
import { useWorkspaces } from '../workspaces';
import { useContactBook } from '../contact-book';
import { channelSlot } from '../contact-rows';
import { REPLAY_WINDOW_MS } from './map-replay';
import { BRIEF_ID, MAESTRO_ID, outreachNodeId } from './map-layout';
import { landingFor, originFor, type KnownNodes, type MapEvent } from './map-events';

/**
 * What ELSE happened in the last day, beyond the AgentAction ledger — so the timeline is honest about the
 * account's day. The ledger only carries what an agent logged as an action; the morning meeting, the
 * notifications the Maestro sent, the tasks, goals, files and apps that moved and the outreach threads
 * that advanced are all real events of the day and all have a node to leave from and one to land on:
 *
 *  - the morning meeting → brief → Maestro (kind `brief`), at the brief's generated time;
 *  - a notification → Maestro → its Space district (kind `notification`), at its date; it opens the
 *    conversation it came from, when it names one;
 *  - a task / goal / file / app that moved → its agent (or the Maestro) → ITS SPACE's district (or
 *    "everything else"), at its update — the district is what a Space's work lands in;
 *  - an outreach thread → Maestro → that channel's outreach node (kind `outreach`), at its last update;
 *  - a reply that arrived and waits on the reader → that channel → the Maestro (kind `message`).
 *
 * Every source is a read the home makes anyway (the brief, the task list, the contact book, the goals,
 * the file list, the apps) or a single cached page (notifications). Pure mapping; mirrored on the phones.
 */
export function useMapHistorySources(known: KnownNodes, drawnAgentIds: string[]): MapEvent[] {
  const brief = useMorningBrief();
  const { state: tasks } = useTasks();
  const goals = useWorkspaceGoals();
  const files = useAgentWorkspaceFiles();
  const workspaces = useWorkspaces();
  const workspaceIds = useMemo(() => (workspaces.status === 'ready' ? workspaces.workspaces.map((w) => w.id) : []), [workspaces]);
  const apps = useAllWorkspaceApps(workspaceIds);
  const book = useContactBook('');
  const notifications = useQuery({ queryKey: queryKeys.homeMapNotifications, queryFn: () => fetchPage(null), staleTime: 60_000 });
  // The memo keys on the DATA arrays (React Query keeps their identity between renders), never on the
  // state wrappers, which some hooks rebuild every render — keyed on those, this recomputed each render,
  // re-seeded the history, and the re-render that caused looped the page into "maximum update depth".
  const taskRows = tasks.kind === 'ready' ? tasks.tasks : null;
  const goalRows = goals.status === 'ready' ? goals.goals : null;
  const fileRows = files.status === 'ready' ? files.items : null;
  const appRows = apps.status === 'ready' ? apps.apps : null;
  const people = book.kind === 'ready' ? book.contacts : null;
  const notificationRows = notifications.data?.notifications ?? null;
  const briefId = brief?.conversationId ?? null;
  const briefAtIso = brief?.generatedDate ?? null;
  const briefText = brief?.name?.trim() || brief?.teaser || '';

  return useMemo(() => {
    const now = Date.now();
    const from = now - REPLAY_WINDOW_MS;
    const inWindow = (iso?: string | null) => {
      if (!iso) return null;
      const at = new Date(iso).getTime();
      return Number.isFinite(at) && at >= from && at <= now ? at : null;
    };
    const events: MapEvent[] = [];
    const space = (workspaceId?: string) => (workspaceId ? { workspaceId } : {});

    const briefAt = briefId ? inWindow(briefAtIso) : null;
    if (briefId && briefAt) events.push({ id: `brief:${briefId}`, at: briefAt, kind: 'brief', from: BRIEF_ID, to: MAESTRO_ID, text: briefText });

    for (const n of notificationRows ?? []) {
      const at = inWindow(n.date);
      if (!at) continue;
      // The notification's own conversation is the thing to open; without one the card carries the text.
      const href = n.conversationId ? `/?conversation=${encodeURIComponent(n.conversationId)}` : undefined;
      events.push({
        id: `notification:${n.id}`,
        at,
        kind: 'notification',
        from: MAESTRO_ID,
        to: landingFor('messages', undefined, known),
        text: [n.title, n.body].filter(Boolean).join(' — '),
        ...(href ? { href } : {}),
      });
    }

    const taskAt: Record<string, number> = {};
    if (taskRows) {
      for (const task of taskRows) {
        const at = inWindow(task.updatedDate || task.createdDate);
        if (!at || !task.id) continue;
        taskAt[task.id] = at;
        events.push({
          id: `task:${task.id}:${at}`,
          at,
          kind: 'task',
          from: originFor(task.agentId, drawnAgentIds),
          to: landingFor('tasks', task.workspaceId, known),
          text: task.title ?? '',
          agentId: task.agentId,
          relatedTaskId: task.id,
          href: `/tasks/${task.id}`,
          ...space(task.workspaceId),
        });
      }
    }

    // A goal carries no date of its own: it moved when its latest task moved.
    for (const goal of goalRows ?? []) {
      const at = Math.max(0, ...goal.tasks.map((task) => taskAt[task.id] ?? 0));
      if (!at) continue;
      events.push({
        id: `goal:${goal.id}:${at}`,
        at,
        kind: 'goal',
        from: MAESTRO_ID,
        to: landingFor('goals', goal.workspaceId, known),
        text: goal.title,
        href: `/goals/${goal.id}`,
        ...space(goal.workspaceId),
      });
    }

    for (const file of fileRows ?? []) {
      const at = inWindow(file.updatedDate || file.createdDate);
      if (!at) continue;
      events.push({
        id: `file:${file.workspaceId ?? '-'}:${file.name}:${at}`,
        at,
        kind: 'file',
        from: MAESTRO_ID,
        to: landingFor('files', file.workspaceId, known),
        text: file.name.split('/').pop() ?? file.name,
        // The file ITSELF (the standalone file page), not the explorer it would have to be found in.
        href: file.workspaceSlug
          ? `/workspace/${encodeURIComponent(file.workspaceSlug)}/files/${encodeURIComponent(file.name)}`
          : file.name.includes('/')
          ? `/files?folder=${encodeURIComponent(file.name.split('/').slice(0, -1).join('/'))}`
          : '/files',
        ...space(file.workspaceId),
      });
    }

    for (const app of appRows ?? []) {
      const at = inWindow(app.updatedDate || app.createdDate);
      if (!at) continue;
      events.push({
        id: `app:${app.id}:${at}`,
        at,
        kind: 'app',
        from: MAESTRO_ID,
        to: landingFor('apps', app.workspaceId, known),
        text: app.name ?? '',
        href: '/apps',
        ...space(app.workspaceId),
      });
    }

    if (people) {
      for (const person of people) {
        const personId = person.contact?.personId;
        const href = personId ? `/?tab=messages&person=${encodeURIComponent(personId)}` : undefined;
        const slugOf = (channel: string | undefined) => {
          const slot = channelSlot(channel);
          const slug = slot === 'gmail_connector' ? 'email' : slot === 'app' || slot === 'other' ? null : slot;
          return slug && known.outreach.includes(slug) ? slug : null;
        };
        for (const thread of person.outbound ?? []) {
          const at = inWindow(thread.updatedAt);
          const slug = slugOf(thread.channel);
          if (!at || !slug) continue;
          events.push({
            id: `outreach:${thread.outreachId}`,
            at,
            kind: 'outreach',
            from: MAESTRO_ID,
            to: outreachNodeId(slug),
            text: [person.contact?.displayName, thread.lastMessage].filter(Boolean).join(' — '),
            ...(href ? { href } : {}),
          });
        }
        // A reply that came back on a channel is drawn ARRIVING: from the channel into the Maestro.
        for (const thread of person.inbound ?? []) {
          const at = inWindow(thread.updatedAt);
          const slug = slugOf(thread.channel);
          if (!at || !slug) continue;
          events.push({
            id: `reply:${thread.outreachId}:${at}`,
            at,
            kind: 'message',
            from: outreachNodeId(slug),
            to: MAESTRO_ID,
            text: [person.contact?.displayName, thread.lastMessage].filter(Boolean).join(' — '),
            ...(href ? { href } : {}),
          });
        }
      }
    }
    return events.sort((a, b) => a.at - b.at);
  }, [briefId, briefAtIso, briefText, notificationRows, taskRows, goalRows, fileRows, appRows, people, known, drawnAgentIds]);
}
